// The pointer and the keyboard (plan C.2 `fast/input.ts`, the port of
// `main.ts:1330-1359`, `:1754-1933` and `:1940-2002`).
//
// This module opens every listener the map needs and does nothing else: it
// turns an event into a call on `Commands` or `Tooltip`. The decisions live
// there, so the mouse grammar and the touch grammar cannot drift apart, and
// the keyboard is sugar over the same calls the on-screen controls make
// (ui-map-interface.md §9 — no key is the only way to do anything).
//
// `ui/touch.ts` and `ui/keymap.ts` are the shipping client's own and are
// reused unchanged. The handler below names no key: it asks `matchKey`
// which binding of `KEYS.map` a press answers and switches on the id, and
// `data/presentation/keybindings.toml` holds every claim (R853).
//
// Two bindings reach past `Commands`. The five layer-view rows (R456, R501)
// flip a field of the `LayerView` the caller owns, and `kraken` calls the
// optional `summon` dep, which is the debug key that raises the trench
// kraken (`main.ts:2293-2295`). `fast/main.ts` writes the view onto the
// layers in the frame, because a hello rebuilds them.

import {
  Building, FleetOp, Mission, ShipClass,
} from '../gen/proto/aquaphobia';
import type { GameState } from '../state';
import type { Overlay, PickMode } from '../ui/overlay';
import { canAnchor } from '../ui/format';
import { loadableKinds, nextRound, pickKinds, roundOrder } from '../ui/rounds';
import { signatureReady } from '../ui/cards/approach';
import { beamReady } from '../ui/cards/beam';
import { isSapperCard, layKeyKind, nextLayKind } from '../ui/cards/sapper';
import { TAP_DEFAULTS, TapRecognizer, resolveTap, tapContext } from '../ui/touch';
import { KEYS, TOUCH_MENU_GUARD_MS } from '../gen/presentation';
import { matchClick, matchKey } from '../keys';
import type { Camera } from '../render/camera';
import { layerToggleForId, type LayerView } from '../ui/layerview';
import { armedPick, type Commands } from './commands';
import { isOwnBase } from './hit';
import type { Tooltip } from './tooltip';
import { DragOrderGesture, newContextMission, overChrome, type ContextMission } from './dragorder';
import { touchLogSink } from './touchlog';
import { hitText, pressText, whoseText, type PressProbe } from './touchgeom';
import type { HelmKey } from './maphelm';
import { helmTakesKeys, onTheMap } from '../ui/focuswalk';
import { toggleDepthLook } from '../depthlook';

/** How far a press may travel and still count as a click, in CSS px. */
const CLICK_SLOP_PX = 6;

/** The panels esc closes, innermost first. One press closes one. The VIEW
 *  panel (R1747) comes after the modals: a card it opened stands over it.
 *  The build card of the context slot comes last, before the selection
 *  it outranks (R1826, review §2.7). */
const ESC_PANELS = ['keysOpen', 'settingsOpen', 'heatOpen', 'audioOpen', 'viewOpen', 'pickOpen', 'infoOpen'] as const;

/** What the keyboard needs of the fleet layer. */
export interface InputFleets {
  describe(fleetId: number): {
    id: number; cls: ShipClass; own: boolean;
    torpedoes: number; reloadSec: number; scanReadySec: number; shareSec: number;
    decoyReadySec: number;
    /** The round pick the anchor key steps through (R2042, R2395). */
    rounds?: readonly { kind: number; count: number }[]; activeRound?: number; firingKind: number;
    /** The charge and the effect the scan key reads on a warship (R2395);
     *  absent on a hull the layer holds no approach state for. */
    approach?: { charge: number; active: boolean } | null;
    /** The items aboard a Sapper and the wait to its next LAY, which the
     *  keys `f` and `n` read (R2710); absent on every other hull. */
    stow?: readonly { kind: number; count: number }[]; layReadySec?: number;
    /** The anchor state: a cable out pins the hull, so `s` does not ask a
     *  pinned Sapper to COLLECT (R2710). 0 is no cable. */
    anchor?: number;
    /** The beam order left on a SET Beacon, which `s` reads (R2858). */
    beamLeftSec?: number;
    /** The owner's account id; the touch log reads it (R1522). */
    ownerPlayer?: number;
  } | null;
}

/** What the input needs of the chart. */
export interface InputMinimap {
  hit(px: number, py: number): { x: number; y: number } | null;
  at(px: number, py: number): { x: number; y: number };
}

export interface InputDeps {
  canvas: HTMLCanvasElement;
  ui: () => Overlay;
  state: GameState;
  camera: Camera;
  commands: Commands;
  tooltip: Tooltip;
  fleets: InputFleets;
  minimap: InputMinimap;
  /** The five hidden view toggles the caller owns (R456). */
  layerView: LayerView;
  /** Raises the trench kraken. The creature layer fills it in (Phase A W2). */
  summon?: () => void;
  /**
   * Is a surface of its own holding the keyboard (R531)?
   *
   * The replay film covers the map and drives Escape, Space and Home
   * itself. While it is up, no key here may reach `Commands`: a press
   * would order a fleet nobody can see on a map nobody is looking at. The
   * POINTER needs no gate — `#film` covers the canvas, so no pointer event
   * reaches these listeners at all.
   */
  suspended?: () => boolean;
  /**
   * The chrome is veiled behind the season summary (R1519,
   * `ui/seasonover.ts`). The map still pans and zooms and the five layer
   * keys still answer, but no other key here acts: every one of them
   * opens, arms or answers a piece of the chrome that is gone.
   */
  veiled?: () => boolean;
  /**
   * Photo mode (R1623, `ui/photomode.ts`). While `on` holds, the map takes
   * no order: a press selects nothing, a right-click, a tap and a drag give
   * no order, the marker chord is quiet and nothing hovers. A drag still
   * pans, and the wheel and a pinch still zoom. A finger's tap calls `tap`,
   * which hides or shows the bar. Shift with p calls `toggle` from the map,
   * and esc calls it while photo mode is on.
   */
  photo?: { on(): boolean; tap(): void; toggle(): void };
  /**
   * The map marker input (R1294): `alt` with a click opens the marker menu,
   * or removes an own marker. A page that wires nothing keeps the plain
   * click for the chord. `ownAt` answers whether an own marker stands under
   * a point: a click or a tap on it opens the peek with REMOVE (R2034).
   */
  markers?: { altClick(cx: number, cy: number): unknown; ownAt(cx: number, cy: number): boolean };
  /**
   * The quick wheel (R1607, R1611): a held comma opens it at the pointer,
   * and the wheel takes the keyboard while it stands. A page that wires
   * nothing leaves the comma to nobody.
   */
  quick?: { keyOpen(): void };
  /**
   * The view and the fleet layer the touch log reads to state a press
   * (R1498). Only a log line reads it, so a page may leave it out.
   */
  probe?: PressProbe;
  /**
   * The helm keys of the map (R2707): the arrows steer the selected own
   * fleet with the STEER order. `key` answers whether it took the press;
   * a page with no helm leaves the arrows to the menu rows. `repeat` is
   * the repeat of a held key. `lift` is the release of a key, and no key
   * lifts every key: the page lost the focus (R3237).
   */
  helm?: { key(id: HelmKey, repeat: boolean): boolean; lift(id?: HelmKey): void };
}

/** The four helm keys of `[keys.card]` (R2707). */
const HELM_KEYS = {
  steer_port: KEYS.card.steer_port,
  steer_starboard: KEYS.card.steer_starboard,
  telegraph_up: KEYS.card.telegraph_up,
  telegraph_down: KEYS.card.telegraph_down,
};

/**
 * The drag order the frame draws (R1208). `fast/main.ts` reads it once a
 * frame and draws the preview while `dragging` holds.
 */
export interface DragOrderView {
  readonly dragging: boolean;
  /** The fleet the drag orders. */
  readonly fleetId: number;
  /** Where the finger is, in CSS px. */
  readonly x: number;
  readonly y: number;
}

/**
 * Opens every map listener. The client lives for the page, so nothing here
 * is ever removed. It answers the drag order, which the frame draws.
 */
export function installInput(d: InputDeps): DragOrderView {
  // When the last touch event of any kind arrived, on the EVENT's own clock
  // (`e.timeStamp`) and never on the clock at the handler (R583). The
  // Android menu guard reads it and the touch handlers write it, so the two
  // share one box rather than a module-level variable. Both sides read the
  // same clock: `Event.timeStamp` is a `DOMHighResTimeStamp` off the same
  // `timeOrigin` as `performance.now()` in every browser this client ships
  // to, so the guard subtracts one from the other safely.
  const touch = { ts: 0 };
  const drag = new DragOrderGesture();
  installClaims(d, drag);
  installHover(d);
  installMouse(d, touch);
  installTouch(d, touch);
  installDragOrder(d, drag);
  installKeyboard(d);
  return drag;
}

/**
 * The two claims on a pointer, asked in this order at `pointerdown`.
 *
 * A drag that starts on the minimap scrubs the camera across the whole
 * world — 168 px of chart is 5320 px of sea, which is exactly the "get me
 * over there" speed the main map cannot offer.
 *
 * A finger or a pen that presses an own fleet starts a drag order (R1208).
 * The press selects that fleet, the camera does not pan under it, and the
 * finger's moves go to the gesture. A mouse keeps the right-click and is
 * never claimed. A press anywhere else still pans, and a second finger
 * while a drag is held is left to the camera, so a pinch still zooms.
 */
function installClaims(d: InputDeps, drag: DragOrderGesture): void {
  // The touch log's hook (R1497): null while the log is off, so each line
  // below is one null test and builds no string. The gesture writes the
  // claim's own lines through the same hook (`installDragOrder`).
  const log = touchLogSink();
  const fleetAt = (x: number, y: number) => d.commands.dragFleetAt(x, y);
  const why = (x: number, y: number) => `no own fleet (${claimWhy(d, x, y)}`;
  const select = (id: number) => d.commands.select({ kind: 'fleet', id });
  d.camera.claim = (x, y, e) => {
    // Photo mode claims nothing: every drag pans (R1623).
    if (d.photo?.on()) return null;
    if (d.minimap.hit(x, y)) {
      if (log) log('claim minimap');
      return (cx, cy) => {
        const p = d.minimap.at(cx, cy);
        d.camera.centerOn(p.x, p.y);
      };
    }
    return drag.claim(e, x, y, CLICK_SLOP_PX, fleetAt, why, select);
  };
}

/**
 * Why `dragFleetAt` answered 0, for the touch log alone (R1497), and the
 * geometry of the press (R1498).
 */
function claimWhy(d: InputDeps, x: number, y: number): string {
  const ui = d.ui();
  return `aim=${ui.aiming ?? '-'} pick=${armedPick(ui) ?? '-'}`
    + ` hit=${hitWhose(d, x, y)})${pressGeom(d, x, y)}`;
}

/**
 * The hit at a screen point, and whose fleet it is, for the touch log
 * alone (R1522).
 */
function hitWhose(d: InputDeps, x: number, y: number): string {
  const hit = d.commands.hitTest(x, y);
  if (hit?.kind !== 'fleet') return hitText(hit);
  const f = d.fleets.describe(hit.id);
  return hitText(hit) + whoseText(
    f && { own: f.own, faction: hit.faction, ownerPlayer: f.ownerPlayer ?? 0 },
    d.state, (id) => d.ui().squadmate(id));
}

/** The press facts of a log line, or nothing on a page with no probe (R1498). */
function pressGeom(d: InputDeps, x: number, y: number): string {
  return d.probe ? ` ${pressText(d.probe, x, y)}` : '';
}

/**
 * The drag order's own listeners (R1208). The claim above starts the press
 * and feeds its moves; these end it.
 *
 * - A second pointer voids the drag and hands the first finger back to the
 *   camera, so the two fingers pinch.
 * - The release gives the context order at the release point, the same
 *   body a right-click runs. A release over the DOM chrome or over the
 *   minimap gives nothing: a finger backs out of a drag by lifting there.
 * - A `pointercancel`, a lost capture and a hidden page void the drag.
 * - A release inside the slop is no drag, and the tap grammar reads it.
 */
function installDragOrder(d: InputDeps, drag: DragOrderGesture): void {
  // The touch log's hook (R1497), and the scratch decision its drop line
  // reads; both stay null while the log is off.
  const log = touchLogSink();
  drag.log = log;
  const logMission: ContextMission | null = log ? newContextMission() : null;
  // A peek that a resting finger pinned has no place once the finger moves
  // on: the drag preview is what the player reads now.
  drag.onStart = () => d.tooltip.dismissPeek();
  drag.onDrop = (x, y, fleetId) => {
    // The selection could have moved while the finger was down (a panel
    // press by a second hand, a hull that sank). The order goes to the
    // fleet the press named, or to nothing.
    const sel = d.state.selection;
    if (sel?.kind !== 'fleet' || sel.id !== fleetId) {
      if (log) log(`drop refused: selection=${sel ? `${sel.kind}:${sel.id}` : 'none'} fleet=${fleetId}`);
      return;
    }
    if (log && logMission) {
      const m = d.commands.contextMission(x, y, logMission);
      log(`drop order kind=${m ? m.kind : 'none (no own selection)'}${m && m.targetId ? ` target=${m.targetId}` : ''}`);
    }
    d.commands.issueContextCommand(x, y);
  };
  const offMap = (x: number, y: number) => {
    if (d.minimap.hit(x, y)) {
      if (log) log('release over the minimap');
      return true;
    }
    return overChrome(d.canvas, x, y, log);
  };
  /** The pointer the drag claimed, so a second finger can hand it back. */
  let claimed: number | null = null;
  d.canvas.addEventListener('pointerdown', (e) => {
    // The camera's own listener ran first and asked the claim, so a press
    // that started a drag already owns it here.
    if (drag.owns(e.pointerId)) { claimed = e.pointerId; return; }
    if (drag.down(e.pointerId) && claimed !== null) d.camera.unclaim(claimed);
    claimed = null;
  });
  d.canvas.addEventListener('pointerup', (e) => {
    if (!drag.owns(e.pointerId)) return;
    claimed = null;
    drag.up(e.pointerId, e.clientX, e.clientY, offMap(e.clientX, e.clientY));
  });
  const voided = (e: PointerEvent) => {
    if (!drag.owns(e.pointerId)) return;
    claimed = null;
    drag.cancel(e.pointerId);
  };
  d.canvas.addEventListener('pointercancel', voided);
  d.canvas.addEventListener('lostpointercapture', voided);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return;
    claimed = null;
    drag.reset();
  });
}

/**
 * A click or a tap on the minimap is navigation, never an order. Answers
 * true when the chart took the press.
 */
function jumpOnMinimap(d: InputDeps, cx: number, cy: number): boolean {
  const jump = d.minimap.hit(cx, cy);
  if (jump) d.camera.centerOn(jump.x, jump.y);
  return jump !== null;
}

// ---------------------------------------------------------------------------
// The hover
// ---------------------------------------------------------------------------

function installHover(d: InputDeps): void {
  // The tooltip lives off canvas `pointermove`, so the moment the pointer
  // crosses onto a DOM panel the canvas goes silent and the last tooltip
  // would freeze on screen — `pointerleave` fires exactly at that boundary.
  d.canvas.addEventListener('pointerleave', () => d.tooltip.leave());
  d.canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType === 'touch') return; // a finger peeks; it never hovers
    if (d.photo?.on()) return; // the picture shows no tooltip (R1623)
    d.tooltip.moved(e.clientX, e.clientY);
  });
  // The peek of a click on open water goes with the focus: a press off the
  // map and a window that loses focus both take it away.
  document.addEventListener('pointerdown', (e) => {
    if (e.target !== d.canvas) d.tooltip.dropWaterPin();
  });
  window.addEventListener('blur', () => {
    d.tooltip.dropWaterPin();
    d.helm?.lift();
  });
  // A held turn key turns the rudder until its release (R3237). The match
  // reads the key alone, so a modifier pressed during the hold does not
  // keep the rudder over.
  window.addEventListener('keyup', (e) => {
    if (!d.helm) return;
    for (const id of Object.keys(HELM_KEYS) as HelmKey[]) {
      if (HELM_KEYS[id].claims.some((c) => c === e.code || c === e.key)) d.helm.lift(id);
    }
  });
}

// ---------------------------------------------------------------------------
// The mouse
// ---------------------------------------------------------------------------

function installMouse(d: InputDeps, touch: { ts: number }): void {
  /** Where the press went down, so a drag is not read as a click. */
  let downAt: { x: number; y: number } | null = null;
  /**
   * The press is the marker chord of `keybindings.toml` (R1294). It is read
   * at the press, because the modifier may come up before the button does.
   */
  let markPress = false;
  /** The click that follows a marker chord must not reach the browser. */
  let swallowClick = false;
  /** A peek of a click on open water stood at the press: the click only
   *  takes it away, so a second click opens the next one. */
  let hadPin = false;

  // Right-click is "never mind": it drops the aim first if one is armed,
  // the selection otherwise — and the browser menu never opens over the map
  // (the DOM overlay keeps its native menus for copy and paste).
  d.canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    // A long press on Android can arrive here as a synthesised menu event.
    // The peek owns that gesture, so drop it before anything reads it.
    // The event's own clock on both sides (R583): a frame that delays this
    // handler must not age the guard out from under the peek.
    if (e.timeStamp - touch.ts < TOUCH_MENU_GUARD_MS) return;
    if (d.photo?.on()) return; // photo mode gives no order (R1623)
    // A right click adds a leg while the plot line stands (R2589).
    if (d.commands.plotLeg?.(e.clientX, e.clientY)) return;
    if (d.commands.cancelModes()) return; // right cancels a command mode
    d.commands.issueContextCommand(e.clientX, e.clientY);
  });

  d.canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'touch') return; // the tap grammar owns touch
    const pinned = d.tooltip.dismissWaterPin(e.clientX, e.clientY);
    if (e.button !== 0) { downAt = null; return; }
    hadPin = pinned;
    markPress = d.markers !== undefined && matchClick(e, KEYS.map) === 'marker';
    // A browser may give alt with a click an action of its own. The chord
    // is the map's, so the default of THIS press alone is prevented.
    if (markPress) e.preventDefault();
    downAt = { x: e.clientX, y: e.clientY };
  });

  d.canvas.addEventListener('pointerup', (e) => {
    if (e.pointerType === 'touch') return; // the tap grammar owns touch
    if (e.button !== 0) return;
    if (!downAt) return;
    const moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
    downAt = null;
    const mark = markPress;
    markPress = false;
    if (moved > CLICK_SLOP_PX) return;
    if (d.photo?.on()) return; // a click in photo mode selects nothing (R1623)

    if (jumpOnMinimap(d, e.clientX, e.clientY)) return;
    if (mark && d.markers) {
      swallowClick = true;
      d.markers.altClick(e.clientX, e.clientY);
      return;
    }
    // Open water peeks on a click (R1959), but only when nothing stood: a
    // click that takes away a water peek, the selection card or the build
    // card opens no new peek, so the reader always has a way out. It is
    // read before `clickMap`, which drops the selection. An own map marker
    // is the exception: its REMOVE stands on no other surface, so a click
    // on it opens the peek after a selection or a card too. A click that
    // takes away a water peek still opens none (R2034).
    const ui = d.ui();
    // A point of interest is no water: the click selects it (R3138).
    const water = d.commands.crosshairFor() === null && d.tooltip.waterAt(e.clientX, e.clientY)
      && !d.commands.markAt(e.clientX, e.clientY) && !d.commands.pointAt(e.clientX, e.clientY);
    const busy = hadPin || d.state.selection !== null || ui.slot.owner() !== null;
    const own = water && !hadPin && d.markers?.ownAt(e.clientX, e.clientY) === true;
    hadPin = false;
    d.commands.clickMap(e.clientX, e.clientY);
    if (!water) return;
    if (!busy || own) { d.tooltip.clickWater(e.clientX, e.clientY); return; }
    if (ui.infoOpen) { ui.infoOpen = false; ui.render(true); }
  });

  d.canvas.addEventListener('click', (e) => {
    if (!swallowClick) return;
    swallowClick = false;
    e.preventDefault();
  });
}

// ---------------------------------------------------------------------------
// Touch (ui/touch.ts)
// ---------------------------------------------------------------------------
//
// A finger has one button and no hover, and iOS never raises `contextmenu`
// over a canvas, so every command above is unreachable by touch. The tap
// grammar carries the whole set instead: a tap looks or orders depending on
// what is selected, and a long press peeks at what a hover would say. Every
// handler is gated per event on `pointerType === 'touch'`, so a mouse never
// reaches any of it.

function installTouch(d: InputDeps, touch: { ts: number }): void {
  const tap = new TapRecognizer();
  /** Drives the long press while the finger is still down. */
  let peekTimer: ReturnType<typeof setTimeout> | null = null;
  const stopPeek = () => {
    if (peekTimer) { clearTimeout(peekTimer); peekTimer = null; }
  };

  // The touch log's hook (R1497). `verdict` notes whether the lift fired a
  // tap or a peek, so a lift that fired neither writes `tap none`.
  const log = touchLogSink();
  let verdict = false;
  tap.onTap = (cx, cy) => {
    if (log) {
      verdict = true;
      log(`tap @${Math.round(cx)},${Math.round(cy)} hit=${hitWhose(d, cx, cy)}`
        + pressGeom(d, cx, cy));
    }
    // A peek the timer pinned while this press was in flight is wrong: the
    // press's own timestamps say it was a tap (R583). Take it down before
    // the tap acts, so the reader never sees a tooltip it did not ask for.
    d.tooltip.dismissPeek();
    // In photo mode a tap hides or shows the bar, and nothing else (R1623).
    if (d.photo?.on()) { d.photo.tap(); return; }
    if (jumpOnMinimap(d, cx, cy)) return;
    // A tap adds a leg while the plot line stands (R2589).
    if (d.commands.plotLeg?.(cx, cy)) return;
    if (d.ui().aiming !== null) { d.commands.tapAim(cx, cy); return; }
    const hit = d.commands.hitTest(cx, cy);
    if (d.commands.runPickMode(cx, cy, hit)) return;
    const mine = d.commands.ownSelection();
    // A point of interest under the finger is a target of its own, and
    // it selects as a site does (R3138). A vent is a gather field, so it
    // is the target a deposit is: with a Dredger selected the tap is a
    // GATHER (R3147). A hull or a site takes the tap first.
    const point = hit ? 0 : d.commands.pointAt(cx, cy, true);
    const target = point ? d.commands.pointTarget(point) : d.commands.tapTarget(hit);
    switch (resolveTap(target, tapContext(mine))) {
      case 'select':
        if (hit) d.commands.select({ kind: hit.kind, id: hit.id });
        else if (point) d.commands.select({ kind: 'point', id: point });
        break;
      // The deposit snap and the R79 DPS gate live in the context command,
      // so a tapped order and a right-clicked one are the same order.
      case 'command': d.commands.issueContextCommand(cx, cy); break;
      // The chart furniture is asked LAST by the finger too (R702). This
      // branch is the only one that wants nothing: a tap on open water with
      // no own fleet selected. An order, a hull and a site all took the tap
      // before it, so a battle mark never costs the player an order.
      // A tap on an own map marker pins the peek with REMOVE, so the
      // finger needs no long press to reach it (R2034).
      case 'deselect':
        if (d.commands.pressMark(cx, cy)) break;
        d.commands.select(null);
        if (d.markers?.ownAt(cx, cy)) d.tooltip.pin(cx, cy);
        // A tap on sea objects picks the nearest and peeks its card (v2
        // §15.6, R2658); a long press lists the items of the cell.
        else d.tooltip.pickObject(cx, cy);
        break;
    }
  };

  // The peek: a long press renders what a mouse would see on hover and pins
  // it, because the finger that asked has to come off to let you read it.
  tap.onLongPress = (cx, cy) => {
    if (log) {
      verdict = true;
      log(`longpress @${Math.round(cx)},${Math.round(cy)} hit=${hitWhose(d, cx, cy)}`);
    }
    if (d.photo?.on()) return;
    d.tooltip.pin(cx, cy);
  };

  d.canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.ts = e.timeStamp;
    d.tooltip.dismissPeek(); // the next touch anywhere takes a pinned peek away
    // A finger never clicks open water, but a DEPOT head pins the water
    // peek of a sea object (R3244): the next touch of the map takes it too.
    d.tooltip.dropWaterPin();
    tap.down(e.pointerId, e.clientX, e.clientY, touch.ts);
    stopPeek();
    // A press that never moves raises no further events, so the peek needs a
    // timer of its own; the recognizer stays the judge of whether it fires.
    // The timer has no event, so it reads the wall clock. That is safe: it
    // can only fire the peek LATE, and a lift whose own timestamps say the
    // press was short still taps (R583).
    peekTimer = setTimeout(() => tap.tick(performance.now()), TAP_DEFAULTS.longPressMs + 10);
  });
  d.canvas.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.ts = e.timeStamp;
    tap.move(e.pointerId, e.clientX, e.clientY, touch.ts);
  });
  d.canvas.addEventListener('pointerup', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.ts = e.timeStamp;
    stopPeek();
    verdict = false;
    tap.up(e.pointerId, e.clientX, e.clientY, touch.ts);
    if (log && !verdict) {
      log(`tap none: moved, voided, or the peek already fired${pressGeom(d, e.clientX, e.clientY)}`);
    }
  });
  d.canvas.addEventListener('pointercancel', (e) => {
    if (e.pointerType !== 'touch') return;
    touch.ts = e.timeStamp;
    stopPeek();
    tap.cancel(e.pointerId);
  });

  // A hidden page is never told the fingers lifted, so the tap reader would
  // count every later press as a second finger. `main.ts` clears the camera
  // on the same event; this clears the recognizer.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) tap.reset();
  });
}

// ---------------------------------------------------------------------------
// The keyboard
// ---------------------------------------------------------------------------

/** The selection is the reader's own hold, whose card carries the hold's
 *  round pick (R2783). */
function ownHoldSelected(state: GameState): boolean {
  const sel = state.selection;
  return sel?.kind === 'site' && isOwnBase(state.sites?.get(sel.id), state.playerId);
}

function installKeyboard(d: InputDeps): void {
  window.addEventListener('keydown', (e) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    // A surface of its own has the keyboard (R531). It is read first, so
    // not even a layer toggle reaches the map behind a replay film.
    if (d.suspended?.()) return;
    // One press answers one binding (R853). A chord the table does not
    // claim matches nothing and stays the browser's, so the handler holds
    // no modifier test of its own; the five layer rows are `altgr` and
    // still answer under AltGr, which is alt or ctrl+alt.
    // ENTER confirms the open picker (assign captain, dive start, loadout).
    // A focused button other than a captain row takes the key natively.
    if (e.key === 'Enter' && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey && !d.veiled?.()) {
      const t = e.target as HTMLElement;
      if ((tag !== 'BUTTON' || t.dataset.act === 'picksel') && d.ui().confirmPick()) {
        e.preventDefault();
        d.ui().render(true);
        return;
      }
    }
    // The helm keys (R2707) are read before the map rows, from the map
    // and from a pointer focus, as Tab is (R2835): with a keyboard focus
    // in the menu, up and down walk its rows (R2083). A press the helm
    // does not take keeps its map meaning.
    if (d.helm && !d.veiled?.() && !d.photo?.on() && helmTakesKeys(e.target)) {
      const hk = matchKey(e, HELM_KEYS);
      if (hk !== null && d.helm.key(hk, e.repeat)) {
        e.preventDefault();
        return;
      }
    }
    const id = matchKey(e, KEYS.map);
    if (id === null) return;
    // Photo mode (R1623): shift with p opens it from the map, and esc or
    // shift with p again leaves it. The layer keys still act under it.
    if (d.photo && (id === 'photo' || (id === 'escape' && d.photo.on()))) {
      e.preventDefault();
      d.photo.toggle();
      return;
    }
    // The depth look (R2713, R2726, §17.3.09): the backslash flips the
    // depth tints between the feathered look and the stepped look for the
    // session; the theme pick is the look on every load. It is a view key,
    // so it acts under the photo mode as the layer keys do.
    if (id === 'depth_look') {
      e.preventDefault();
      toggleDepthLook();
      return;
    }
    // The roster answers the matched id once. Its five fields are also
    // the keys that still act while the chrome is veiled (R1519, R2682).
    const layerField = layerToggleForId(id);
    if (d.veiled?.() && layerField === null) return;
    const ui = d.ui();
    const state = d.state;

    // While the plot line stands, enter and `d` press DONE of the line
    // (R3185): the legs go out as the course and the line ends. With no
    // leg, DONE is off and the press does nothing. The two
    // keys keep their other meanings (the menu focus, the base tubes)
    // while no plot line stands. Enter on a focused control presses that
    // control (R2082), so enter acts from the map alone.
    if (ui.plotting?.() && (id === 'base_tubes' || (id === 'menu_focus' && onTheMap(e.target)))) {
      e.preventDefault();
      ui.confirmPlot();
      return;
    }

    if (layerField !== null) {
      d.layerView[layerField] = !d.layerView[layerField];
      return;
    }

    switch (id) {
      case 'escape': {
        const open = ESC_PANELS.find((p) => ui[p]);
        if (open) { ui[open] = false; ui.render(true); }
        // Esc puts the rename card, the naming sheet and the cache sheet
        // away before it backs out of the selection.
        else if (ui.closeNameCard()) return;
        // Esc ends the plot line and sends nothing, as its ✕ does (R2589).
        else if (ui.plotting?.()) ui.endPlot();
        // The hint promises esc backs out of every pick mode; `cancelModes` is
        // the one cascade the CANCEL press and a right-click also run.
        else if (!d.commands.cancelModes()) {
          // With no panel, no pick mode and no selection left to back out
          // of, esc opens the OPTIONS card; the next esc closes it.
          if (state.selection) d.commands.select(null);
          else { ui.settingsOpen = true; ui.audioOpen = false; ui.render(true); }
        }
        return;
      }
      case 'base_tubes': {
        // Base torpedoes fire manually (R82): the key arms the home banks
        // from anywhere, when they exist and hold a round; again disarms.
        //
        // The parity port copied the reader R469 had already retired: the
        // FIRST from_base torpedo alone, so a hold that stocked interceptors
        // and no Heavy answered nothing. One reader on `state` serves the key
        // and the panel, and the key arms the torpedo the panel would fire:
        // the pill pick, or the first stocked one when the pick is not
        // stocked (R533).
        if (ui.aiming === 0) { d.commands.setAiming(null); return; }
        if (state.level(Building.BUILDING_TORPEDOBANKS) === 0) return;
        const stocked = state.stockedHoldRounds();
        if (stocked.length === 0) return;
        if (!stocked.some((t) => t.id === ui.baseTorp)) ui.baseTorp = stocked[0].id;
        d.commands.setAiming(0);
        return;
      }
      // `n` on the hold (R2783): while the hold aim is armed or the own hold
      // is the selection, the key steps the hold's round pick through the
      // stocked hold rounds, as it steps the round of a hull. An open
      // blackjack hand took the press first (`preventDefault`), and a press
      // the hold does not take falls through to the selected hull below.
      case 'casino_stand': {
        if (e.defaultPrevented) return;
        if (ui.aiming === 0 || ownHoldSelected(state)) {
          const ids = state.stockedHoldRounds().map((t) => t.id);
          const next = nextRound(ids, ui.baseTorp);
          if (next) { ui.baseTorp = next; ui.render(true); }
          return;
        }
        break;
      }
      // The kraken moved off `k` (R456): the layer toggles wanted the three
      // keys under the right hand, and a debug creature is the cheaper move.
      case 'kraken':
        d.summon?.(); // §9 debug key
        return;
      // The held comma opens the quick wheel (R1611). The key repeats while
      // it is held, and the open wheel swallows the repeats.
      case 'quick':
        if (!e.repeat) {
          e.preventDefault();
          d.quick?.keyOpen();
        }
        return;
    }

    // Order keys act on the selected own fleet, mirroring the fleet panel's
    // buttons — the same gates the buttons use, and the server re-validates
    // everything anyway.
    if (state.selection?.kind !== 'fleet') return;
    const f = d.fleets.describe(state.selection.id);
    if (!f?.own) return;
    const spec = state.ship(f.cls);
    // The key arms the mode the way the button does (R469).
    const pick = (mode: PickMode) => ui.armPick(mode, f.id);
    // The Sapper takes the keys of the acts it has no use for (R2710): it
    // carries no tubes, no scan and one round of nothing, so `f` lays, `s`
    // collects and `n` picks the next kind, and no key names two acts.
    // The flag gates every Sapper key, as it gates the card.
    const sapper = isSapperCard(state, f.cls);
    switch (id) {
      case 'fire': // aim the tubes (the FIRE button's gate)
        // LAY with the press's gates: a kind aboard and no lay wait.
        if (sapper) {
          const kind = layKeyKind(f, ui.lastLayOf(f.id));
          if (kind) {
            ui.setLastLay(f.id, kind);
            d.commands.fleetOp(f.id, FleetOp.FLEET_OP_LAY, kind);
          }
          break;
        }
        // The Vedette carries no tubes, so `f` drops its A6 noisemaker
        // (R1507, R2696) with the DECOY button's gates: the node and no
        // wait.
        if (f.cls === ShipClass.SHIP_CLASS_VEDETTE) {
          if (f.decoyReadySec === 0 && state.hasTechCode('A6')) {
            d.commands.fleetOp(f.id, FleetOp.FLEET_OP_DECOY);
          }
          break;
        }
        // A stowing hull (the Tender, R712) holds rounds it may not fire:
        // the catalog's fired kinds decide, as they decide the button.
        if ((spec?.torpedoes.length ?? 0) > 0 && f.torpedoes > 0 && f.reloadSec === 0) {
          d.commands.setAiming(f.id);
        }
        break;
      case 'hunt': // gun-carrying hulls only (R79)
        if ((spec?.dps ?? 0) > 0) pick('hunting');
        break;
      // Under the flag `routes` the key of PATROL opens the plot line, as
      // PLOT replaces PATROL in ⋯ (v2 §7, R2589).
      case 'patrol':
        if (state.hasFeatureFlag('routes')) ui.enterPlot(f.id);
        else pick('patrolling');
        break;
      case 'escort': pick('escorting'); break;
      case 'move': pick('moving'); break;
      // Shift with v takes the on-ramp offer of the card (R3075), as
      // VIA LANE does; with no offer it does nothing.
      case 'lane': if (d.commands.takeLaneOffer(f.id)) ui.render(true); break;
      // Gather is the Dredger's mission alone, so the key follows the button
      // and appears for nothing else (R469).
      case 'gather': if (f.cls === ShipClass.SHIP_CLASS_DREDGER) pick('gathering'); break;
      case 'defend': d.commands.setMission(f.id, Mission.MISSION_DEFEND); break;
      case 'return': d.commands.fleetOp(f.id, FleetOp.FLEET_OP_RETURN); break;
      // `q` is the anchor alone, one lever both ways (R114, R661, R2696).
      // Under currents every hull takes it (R2695).
      case 'anchor':
        if (canAnchor(spec)) d.commands.fleetOp(f.id, FleetOp.FLEET_OP_ANCHOR);
        break;
      // `n` switches a hull of two or more round kinds (the Lancer) to the
      // next pill of its round pick (R2042, R2395, R2696). The key is the
      // blackjack STAND too: the shell's handler runs first and calls
      // `preventDefault()` when an open hand took the press, so the map
      // skips it and the press acts once.
      case 'casino_stand': {
        if (e.defaultPrevented) break;
        // A Sapper steps the kind LAY lays among the kinds aboard (R2710).
        // The kind is the client's, as a pill of the lay pick sets it.
        if (sapper) {
          const kind = nextLayKind(f.stow, ui.lastLayOf(f.id));
          if (kind) ui.setLastLay(f.id, kind);
          break;
        }
        const torps = state.catalog?.torpedoes ?? [];
        const kinds = pickKinds(loadableKinds(spec, torps), f.rounds ?? [], roundOrder(torps));
        const next = kinds.length >= 2 ? nextRound(kinds, f.activeRound || f.firingKind) : 0;
        if (next) d.commands.fleetOp(f.id, FleetOp.FLEET_OP_SET_ROUND, next);
        break;
      }
      // One key for four acts: a hull is a Vedette, an Oracle, a Beacon or
      // a warship with a signature order, never two of them, so `s` names
      // one act for any fleet. The Oracle takes the SHARE button's gates
      // (R453): the Signal Station level and no broadcast in flight. A
      // warship spends its signature order with the press's gates
      // (R2395): the approach state, a READY charge and no effect that
      // holds. The server re-validates the spend.
      case 'scan':
        // A Sapper collects (R2710), with the gate of COLLECT: a cable out
        // pins the hull. The server refuses a hull that moves.
        if (sapper) {
          if (!f.anchor) d.commands.fleetOp(f.id, FleetOp.FLEET_OP_COLLECT);
        } else if (f.cls === ShipClass.SHIP_CLASS_VEDETTE) {
          if (f.scanReadySec === 0) d.commands.fleetOp(f.id, FleetOp.FLEET_OP_SCAN); // off cooldown (R83)
        } else if (f.cls === ShipClass.SHIP_CLASS_ORACLE) {
          if (f.shareSec === 0
            && state.level(Building.BUILDING_COMMUNICATIONS) >= state.rules.oracleShareComms) {
            d.commands.fleetOp(f.id, FleetOp.FLEET_OP_SHARE_SONAR);
          }
        } else if (f.cls === ShipClass.SHIP_CLASS_BEACON) {
          // The beam order of a SET Beacon, with the press's gate (R2858).
          if (beamReady({ anchor: f.anchor ?? 0, beamLeftSec: f.beamLeftSec ?? 0 })) {
            d.commands.fleetOp(f.id, FleetOp.FLEET_OP_BEAM);
          }
        } else if (signatureReady(spec?.signature, f.approach)) {
          d.commands.fleetOp(f.id, FleetOp.FLEET_OP_SIGNATURE);
        }
        break;
      // Hold fire (R1437): the HOLD FIRE button's toggle, on a hull with a
      // gun. The selection is one fleet, so the key toggles that fleet.
      case 'hold_fire':
        if ((spec?.dps ?? 0) > 0) d.commands.fleetOp(f.id, FleetOp.FLEET_OP_HOLD_FIRE);
        break;
    }
  });
}
